import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { createHttpParamsFromRecord } from '../secure/common/helpers';
import {
  AddCameraRequest,
  AddKioskRequest,
  ArchivedLocation,
  Branch,
  BranchCameraWithCameras,
  BranchDetailsResponse,
  BranchLocationsShrinkResponse,
  BranchSetting,
  CreateBranchRequest,
  CreateShrinkRequest,
  GetBranchesRequest,
  GetBranchShrinkRequest,
  HasItems,
  Kiosk,
  KioskProvider,
  KioskTransaction,
  LocationAttribute,
  LocationCamera,
  LocationInfo,
  LocationShrinkResponse,
  LocationTheftReport,
  LocationUpdateRequest,
  MergeLocationsRequest,
  MoveLocationsToBranchRequest,
  ProvidersResponse,
  ReviewManagementBranch,
  UpdateBranchAllowedDomainsRequest,
  UpdateBranchBusinessVerticalRequest,
  UpdateBranchRequest,
  UpdateShrinkRequest,
  UploadLocationsShrinkCsvRequest,
} from '../types';

@Injectable({ providedIn: 'root' })
export class BranchService {
  apiHost = `${environment.apiBaseUrl}`;
  private httpClient = inject(HttpClient);

  constructor() {}

  /**
   * @deprecated use LocationService#getLocations instead
   */
  deprecated_getLocationsForBranch(branchId: number): Observable<LocationInfo[]> {
    return this.httpClient.get<LocationInfo[]>(`${this.apiHost}/CustomerPortal/Branches/${branchId}/Locations`);
  }

  createBranch(data: CreateBranchRequest): Observable<Branch> {
    const requestUrl = `${this.apiHost}/Branches`;
    return this.httpClient.post<Branch>(requestUrl, data);
  }

  createBranchLocation(
    branchId: number,
    newLocation: { name: string; timezone: string; is_national_account?: boolean },
  ): Observable<{ id: number }> {
    return this.httpClient.post<{ id: number }>(`${this.apiHost}/Branches/${branchId}/Locations`, newLocation);
  }

  getBranches(request?: GetBranchesRequest): Observable<HasItems<Branch>> {
    let params = createHttpParamsFromRecord({
      include_camera_providers: request?.include_camera_providers ? 'true' : 'false',
      include_kiosk_providers: request?.include_kiosk_providers ? 'true' : 'false',
      include_queue: request?.include_queue ? 'true' : 'false',
      count_incidents: request?.count_incidents ? 'true' : 'false',
    });

    if (request?.isf) {
      params = params.set('isf', request.isf ? 'true' : 'false');
    }

    if (request?.market_snapshots_enabled) {
      params = params.set('market_snapshots_enabled', request.market_snapshots_enabled ? 'true' : 'false');
    }

    if (request?.vertical) {
      params = params.set('vertical', request.vertical);
    }

    return this.httpClient.get<HasItems<Branch>>(`${this.apiHost}/Branches`, { params });
  }

  getBranchById(branchId: number, request?: GetBranchesRequest): Observable<Branch> {
    const params = createHttpParamsFromRecord({
      include_camera_providers: request?.include_camera_providers ? 'true' : 'false',
      include_kiosk_providers: request?.include_kiosk_providers ? 'true' : 'false',
      include_queue: request?.include_queue ? 'true' : 'false',
      count_incidents: request?.count_incidents ? 'true' : 'false',
    });
    return this.httpClient.get<Branch>(`${this.apiHost}/Branches/${branchId}`, { params });
  }

  getBranchUsage(branchId: number): Observable<ReviewManagementBranch> {
    return this.httpClient.get<ReviewManagementBranch>(`${this.apiHost}/Branches/${branchId}/Usage`);
  }

  getBranchDetails(branchId: number): Observable<BranchDetailsResponse> {
    return this.httpClient.get<BranchDetailsResponse>(`${this.apiHost}/Branches/${branchId}/Details`);
  }

  getBranchKioskMostRecentTransactions(
    branchId: number,
    kioskId?: number,
    kioskName?: string,
    branchAliasName?: string,
  ): Observable<HasItems<KioskTransaction>> {
    const hasId = kioskId != null;
    const hasName = kioskName != null && kioskName.trim().length > 0;

    if (!hasId && !hasName) {
      throw new Error('Either kioskId or kioskName must be provided');
    }
    const kiosk = hasId ? kioskId : kioskName.trim();
    let params = new HttpParams();
    if (branchAliasName) params = params.set('branchAliasName', branchAliasName);

    return this.httpClient.get<HasItems<KioskTransaction>>(`${this.apiHost}/Branches/${branchId}/Kiosks/${kiosk}/MostRecentTransactions`, {
      params,
    });
  }

  getCameraSnapshot(branchId: number, providerBranchId: string, providerCameraId: string): Observable<Blob> {
    return this.httpClient.get(`${this.apiHost}/Branches/${branchId}/Providers/${providerBranchId}/Cameras/${providerCameraId}/Snapshot`, {
      responseType: 'blob',
    });
  }

  updateBranch(branchId: number, request: UpdateBranchRequest): Observable<Branch> {
    return this.httpClient.put<Branch>(`${this.apiHost}/Branches/${branchId}`, request);
  }

  updateBranchBusinessVertical(branchId: number, request: UpdateBranchBusinessVerticalRequest): Observable<Branch> {
    return this.httpClient.put<Branch>(`${this.apiHost}/Branches/${branchId}/BusinessVertical`, request);
  }

  updateBranchSetting(branch_id: number, setting: BranchSetting, enabled: boolean) {
    const requestUrl = `${this.apiHost}/Branches/${branch_id}/Settings`;
    return this.httpClient.patch<void>(requestUrl, { setting, enabled });
  }

  // this will set the entire list, so pass the full list of allowed domains you want, not just the ones you want to add/remove
  updateBranchAllowedDomains(branchId: number, request: UpdateBranchAllowedDomainsRequest): Observable<Branch> {
    return this.httpClient.put<Branch>(`${this.apiHost}/Branches/${branchId}/AllowedDomains`, request);
  }

  updateBranchLocation(branchId: number, locationId: number, request: LocationUpdateRequest) {
    return this.httpClient.put(`${this.apiHost}/Branches/${branchId}/Locations/${locationId}`, request);
  }

  updateBranchLocationAttribute(branchId: number, locationId: number, attribute: LocationAttribute, enabled: boolean) {
    const requestUrl = `${this.apiHost}/Branches/${branchId}/Locations/${locationId}/Attributes`;
    return this.httpClient.patch(requestUrl, { attribute, enabled });
  }

  // this method is not in the "BranchesController" so IDK if we should create a service just for this one
  getProviders(): Observable<ProvidersResponse> {
    return this.httpClient.get<ProvidersResponse>(`${this.apiHost}/Providers`);
  }

  addBranchCameraProvider(branchId: number, camera_provider: string, provider_branch_id: string) {
    return this.httpClient.post<BranchCameraWithCameras>(`${this.apiHost}/Branches/${branchId}/Cameras/Providers`, {
      camera_provider,
      provider_branch_id,
    });
  }

  // providerId refers to the branch_camera id (uuid), not the provider_branch_id
  removeBranchCameraProvider(branchId: number, providerId: string) {
    return this.httpClient.delete(`${this.apiHost}/Branches/${branchId}/Cameras/Providers/${providerId}`);
  }

  addKioskProvider(branchId: number, provider: KioskProvider) {
    return this.httpClient.post<KioskProvider>(`${this.apiHost}/Branches/${branchId}/Kiosks/Providers`, provider);
  }

  // providerId refers to the branch_alias id
  removeKioskProvider(branchId: number, providerId: number) {
    return this.httpClient.delete(`${this.apiHost}/Branches/${branchId}/Kiosks/Providers/${providerId}`);
  }

  moveLocationsToBranch(branchId: number, request: MoveLocationsToBranchRequest): Observable<void> {
    const requestUrl = `${this.apiHost}/Branches/${branchId}/Locations/Move`;
    return this.httpClient.post<void>(requestUrl, request);
  }

  // when location 1 is merged into location 2, location 1 becomes archived
  // and can be considered is_deleted = true even tho it doesn't have this prop
  mergeLocations(branchId: number, request: MergeLocationsRequest): Observable<ArchivedLocation> {
    const requestUrl = `${this.apiHost}/Branches/${branchId}/Locations/Merge`;
    return this.httpClient.post<ArchivedLocation>(requestUrl, request);
  }

  getBranchLocationsReviewsWithRange(
    branchId: number,
    dateFrom: Date | undefined,
    dateTo: Date | undefined,
  ): Observable<LocationTheftReport[]> {
    const requestUrl = `${this.apiHost}/ReportPortal/Branches/${branchId}/MostRecentReviews`;
    const params = {
      date_from: DateTime.fromJSDate(dateFrom).toFormat('yyyy-MM-dd'),
      date_to: DateTime.fromJSDate(dateTo).toFormat('yyyy-MM-dd'),
    };
    return this.httpClient.get<LocationTheftReport[]>(requestUrl, { params });
  }

  getBranchAvailableCameras(branchId: number): Observable<HasItems<LocationCamera>> {
    return this.httpClient.get<HasItems<LocationCamera>>(`${this.apiHost}/Branches/${branchId}/AvailableCameras`);
  }

  getBranchAvailableKiosks(branchId: number): Observable<HasItems<Kiosk>> {
    return this.httpClient.get<HasItems<Kiosk>>(`${this.apiHost}/Branches/${branchId}/AvailableKiosks`);
  }

  getBranchAvailableCamerasForEdgeDevices(branchId: number, locationId: number): Observable<HasItems<LocationCamera>> {
    return this.httpClient.get<HasItems<LocationCamera>>(
      `${this.apiHost}/Branches/${branchId}/Locations/${locationId}/AvailableCamerasForEdgeDevices`,
    );
  }

  addKioskToLocation(branchId: number, locationId: number, request: AddKioskRequest): Observable<Kiosk> {
    const requestUrl = `${this.apiHost}/Branches/${branchId}/Locations/${locationId}/Kiosks`;
    return this.httpClient.post<Kiosk>(requestUrl, request);
  }

  removeKioskFromLocation(branchId: number, locationId: number, kioskId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.apiHost}/Branches/${branchId}/Locations/${locationId}/Kiosks/${kioskId}`);
  }

  addCameraToLocation(branchId: number, locationId: number, request: AddCameraRequest): Observable<HasItems<LocationCamera>> {
    const requestUrl = `${this.apiHost}/Branches/${branchId}/Locations/${locationId}/Cameras`;
    return this.httpClient.post<HasItems<LocationCamera>>(requestUrl, request);
  }

  // locationCameraId is the guid/uuid in panoptyc, not the provider_camera_id, the 'id' property in LocationCamera
  removeCameraFromLocation(branchId: number, locationId: number, locationCameraId: string): Observable<HasItems<LocationCamera>> {
    return this.httpClient.delete<HasItems<LocationCamera>>(
      `${this.apiHost}/Branches/${branchId}/Locations/${locationId}/Cameras/${locationCameraId}`,
    );
  }

  getBranchLocationsShrink(branchId: number, request: GetBranchShrinkRequest): Observable<BranchLocationsShrinkResponse> {
    const params = createHttpParamsFromRecord({
      year: request.year.toString(),
      month: request.month.toString(),
    });
    return this.httpClient.get<BranchLocationsShrinkResponse>(`${this.apiHost}/Branches/${branchId}/Shrink`, { params });
  }

  createBranchLocationShrink(branchId: number, locationId: number, request: CreateShrinkRequest): Observable<LocationShrinkResponse> {
    return this.httpClient.post<LocationShrinkResponse>(`${this.apiHost}/Branches/${branchId}/Locations/${locationId}/Shrink`, request);
  }

  updateBranchLocationShrink(
    branchId: number,
    locationId: number,
    locationShrinkId: string,
    request: UpdateShrinkRequest,
  ): Observable<LocationShrinkResponse> {
    return this.httpClient.put<LocationShrinkResponse>(
      `${this.apiHost}/Branches/${branchId}/Locations/${locationId}/Shrink/${locationShrinkId}`,
      request,
    );
  }

  deleteBranchLocationShrink(branchId: number, locationId: number, locationShrinkId: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.apiHost}/Branches/${branchId}/Locations/${locationId}/Shrink/${locationShrinkId}`);
  }

  uploadBranchesShrinkCsv(request: UploadLocationsShrinkCsvRequest): Observable<HasItems<BranchLocationsShrinkResponse>> {
    const formData = new FormData();
    formData.append('csv', request.csv);
    return this.httpClient.post<HasItems<BranchLocationsShrinkResponse>>(`${this.apiHost}/Branches/Shrink/Csv`, formData);
  }
}
